import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Trash2, Mail, Users } from 'lucide-react';
import { newsletterApi } from '../../api/endpoints/newsletter';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { StatCard } from '../../components/ui/StatCard';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Alert';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { PERMISSIONS } from '../../lib/permissions';
import { formatDate } from '../../lib/formatters';
import type { NewsletterSubscriber } from '../../types/newsletter';

export function NewsletterPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [deleteTarget, setDeleteTarget] = useState<NewsletterSubscriber | null>(null);
  const { page, limit, goToPage } = usePagination({ resetDeps: [debouncedSearch] });

  const { data, isLoading } = useQuery({
    queryKey: ['newsletter', storeId, { page, limit, search: debouncedSearch }],
    queryFn: () => newsletterApi.list(storeId!, { page, limit, search: debouncedSearch || undefined }),
    enabled: !!storeId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => newsletterApi.delete(storeId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter', storeId] });
      toast('success', t('newsletter.deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleExport = async () => {
    try {
      const result = await newsletterApi.exportCsv(storeId!);
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter-subscribers-${storeId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast('success', t('common.success'));
    } catch {
      toast('error', t('common.error'));
    }
  };

  const columns: Column<NewsletterSubscriber>[] = [
    {
      key: 'email',
      header: t('common.email'),
      render: (s) => (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-text-muted" />
          <span className="font-medium">{s.email}</span>
        </div>
      ),
    },
    {
      key: 'name',
      header: t('common.name'),
      render: (s) => <span className="text-text-secondary">{s.name ?? '-'}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (s) => (
        <Badge variant={s.isActive ? 'success' : 'default'}>
          {s.isActive ? t('newsletter.activeSubscribers') : t('coupons.inactive')}
        </Badge>
      ),
    },
    {
      key: 'date',
      header: t('newsletter.subscribedAt'),
      sortable: true,
      render: (s) => <span className="text-xs text-text-secondary">{formatDate(s.subscribedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (s) => (
        <PermissionGate requires={PERMISSIONS.NEWSLETTER_EDIT}>
          <button
            onClick={() => setDeleteTarget(s)}
            className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </PermissionGate>
      ),
    },
  ];

  const subscribers = data?.subscribers ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;
  // Use total from pagination as a proxy for active count (most subscribers are active)
  // Computing from current page only would be misleading for paginated results
  const activeCount = (data as any)?.activeCount ?? total;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('newsletter.title')}</h1>
        <PermissionGate requires={PERMISSIONS.NEWSLETTER_EDIT}>
          <Button variant="secondary" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4" />
            {t('newsletter.exportCsv')}
          </Button>
        </PermissionGate>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        <StatCard
          title={t('newsletter.totalSubscribers')}
          value={total}
          icon={<Users className="h-5 w-5" />}
        />
        <StatCard
          title={t('newsletter.activeSubscribers')}
          value={activeCount}
          icon={<Mail className="h-5 w-5" />}
        />
      </div>

      <div className="mb-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search') + '...'}
          className="max-w-sm"
        />
      </div>

      <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={subscribers}
          isLoading={isLoading}
          keyExtractor={(s) => s._id}
          emptyTitle={t('newsletter.empty')}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('newsletter.deleteConfirm')}</p>
        <p className="mt-2 font-medium">{deleteTarget?.email}</p>
      </Modal>
    </div>
  );
}
